/* eslint-disable lingui/no-unlocalized-strings */
import {
  IS_LOCAL_CYPRESS,
  IS_LOCAL_DEV_NO_CY_NO_EMULATE,
  isPRPreview,
  KnownDomains,
} from '../../utils/constants';
import {
  Config,
  configAliases,
  getPRDomainFromStorage,
  hostnameIsV2Tenant,
  localConfig,
} from '../base';
import { findEnvironmentByHostname } from './tenants.config';

/**
 * Registry-driven config builder.
 *
 * Produces the same `Config` shape as the legacy `getConfigFromUrl`, but sources
 * the non-derivable values (flatfile keys, LaunchDarkly key, envKey) from the
 * tenant registry (`tenants.config.ts`) instead of the hand-maintained domain
 * maps in `./legacy`. Every endpoint and keycloak value is derived by templating
 * the resolved hostname exactly as the legacy system does, so the two builders
 * are byte-for-byte interchangeable (enforced by `config-parity.test.ts`).
 *
 * The localhost / cypress / PR-preview branches reuse the shared primitives in
 * `./base`, so those paths stay identical to the legacy builder.
 */
export const buildConfigFromRegistry = (
  hostname: string,
): Config | undefined => {
  let base = typeof hostname === 'string' ? hostname : '';
  const foundFromResolutions = Object.entries(configAliases).find(
    ([, testArr]) => testArr.includes(base as KnownDomains),
  )?.[0];
  if (foundFromResolutions) {
    base = foundFromResolutions;
  }
  let useV2 = false;
  if (hostnameIsV2Tenant(hostname)) {
    base = hostname;
    useV2 = true;
  }
  if (base === 'localhost' || IS_LOCAL_CYPRESS) {
    // If we know we are in local, on a regular browser (not cy), AND that the
    // contributor does not want to emulate an env, we use internal dev config.
    if (IS_LOCAL_DEV_NO_CY_NO_EMULATE) {
      return buildConfigFromRegistry('dev.mm100.mastermindtms.com');
    }
    return localConfig;
  } else if (isPRPreview(base)) {
    return buildConfigFromRegistry(
      getPRDomainFromStorage() ?? 'dev.mm100.mastermindtms.com',
    );
  } else if (!base.match(/mastermindtms\.com/)) {
    return undefined;
  }

  const match = findEnvironmentByHostname(hostname);
  if (!match) {
    return undefined;
  }
  const { environment } = match;

  const isDev = Boolean(base.match(/^dev\./));
  const isTest = Boolean(base.match(/^test\./));
  const isPreview = isDev || isTest;

  let tenantId = base
    .replace(/dev\./, '')
    .replace(/test\./, '')
    .replace(/\.mastermindtms\.com/, '');
  if (useV2) {
    tenantId = hostname;
  }

  let keycloakUri = `https://id.${tenantId}${
    isPreview ? 'non' : ''
  }prod.mastermindtms.com/auth`;

  if (useV2) {
    const kcBase = base
      .replace(/dev\./, '')
      .replace(/test\./, '')
      .replace(/\.mastermindtms\.com/, '');
    keycloakUri = `https://id.${kcBase}${
      isPreview ? '-nonprod' : ''
    }.mastermindtms.com/auth`;
  }

  const obj: Config = {
    launchDarklyKey: environment.launchDarklyKey ?? '',
    apiEndpoint: `https://graphql.${base}/`,
    minionApiRestEndpoint: `https://minion-api.${base}`,
    dataDictionaryEndpoint: `https://minion-api.${base}/graphql`,
    subscriptionApiEndpoint: `wss://subscriptions.${base}/graphql`,
    uploadsApiEndpoint: `https://uploads.${base}`,
    restApiEndpoint: `https://rest.${base}`,
    documentApiEndpoint: `https://document-api.${base}`,
    keycloakUri,
    keycloakClientId: `https://${base}/`,
    keycloakRealm: base,
    caenLockingApiEndpoint: `https://caenlocking-api.${base}`,
    emailTenderingEndpoint: `https://tendering-email-api.${base}`,
    aiEndpoint: `https://ai.${base}`,
    alchemyDataEndpoint: `https://alchemy-data-api.${base}`,
    regionsDataEndpoint: `https://regions-v2-api.${base}`,
    flatFileLicenseKey: environment.flatFileLicenseKey,
    flatFileApiEndPoint: `https://flatfile.${base}`,
    ltlPricingMatrix: `https://ltl-pricing-matrix.${base}`,
    masterbidEndpoint: `https://masterbid.${base}`,
    flatFilePlatformPublishableKey: environment.flatFilePublishableKey,
    flatFilePlatformEnvironmentId: environment.flatFileEnvironmentId,
    envKey: environment.envKey,
  };
  if (tenantId === 'sn100' && isPreview) {
    obj.keycloakUri = 'https://id.clientnonprod.mastermindtms.com/auth';
  }
  return obj;
};
